import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleOff,
  Download,
  FileJson,
  FileSpreadsheet,
  FileText,
  Link2,
  LoaderCircle,
  Pause,
  Pencil,
  Play,
  RefreshCw,
  ShieldCheck,
  Star,
  Trash2,
  Users,
  Video,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { errorMessage } from '../../lib/api';
import { queryKeys } from '../../lib/queryClient';
import { eventsSchema } from '../../lib/schemas';
import { getSocket } from '../../lib/socket';
import type { EventSummary } from '../../types';
import {
  archiveEvent,
  createEvent,
  getEvents,
  getZoomConnectionStatus,
  styleEvent,
  toggleEvent,
  updateEvent,
  verifyEvent,
  type EventCreateInput,
  type EventStyleInput,
  type EventUpdateInput,
} from './eventsApi';
import { ModulePage } from './ModulePage';

type EventDialog =
  | { kind: 'edit'; event: EventSummary }
  | { kind: 'schedule'; event: EventSummary }
  | { kind: 'link'; event: EventSummary }
  | null;

type EventAction =
  | { kind: 'toggle' | 'verify' | 'archive'; event: EventSummary }
  | { kind: 'style'; event: EventSummary; input: EventStyleInput; message: string }
  | { kind: 'update'; event: EventSummary; input: EventUpdateInput }
  | { kind: 'schedule'; event: EventSummary; scheduledAt: number }
  | { kind: 'link'; event: EventSummary; joinUrl: string };

interface StatusMessage {
  tone: 'ok' | 'error';
  text: string;
}

const eventStatusLabels: Record<string, string> = {
  started: 'Live',
  waiting: 'Waiting',
  ended: 'Ended',
  unknown: 'Scheduled',
};

export function EventsManager() {
  const queryClient = useQueryClient();
  const [viewDate, setViewDate] = useState<number | null>(null);
  const [today] = useState(() => localMidnight(Date.now()));
  const [dialog, setDialog] = useState<EventDialog>(null);
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [addFormVersion, setAddFormVersion] = useState(0);
  const events = useQuery({
    queryKey: queryKeys.events,
    queryFn: ({ signal }) => getEvents(signal),
    refetchInterval: 30_000,
  });
  const zoomStatus = useQuery({
    queryKey: queryKeys.zoomStatus,
    queryFn: ({ signal }) => getZoomConnectionStatus(signal),
    staleTime: 30_000,
  });

  useEffect(() => {
    const socket = getSocket();
    const updateEvents = (payload: EventSummary[]) => {
      const parsed = eventsSchema.safeParse(payload);
      if (parsed.success) queryClient.setQueryData(queryKeys.events, parsed.data);
    };
    socket.on('events_update', updateEvents);
    return () => { socket.off('events_update', updateEvents); };
  }, [queryClient]);

  const action = useMutation({
    mutationFn: executeEventAction,
    onSuccess: async result => {
      setDialog(null);
      setStatus({ tone: 'ok', text: result.message });
      await queryClient.invalidateQueries({ queryKey: queryKeys.events });
    },
    onError: error => setStatus({ tone: 'error', text: errorMessage(error, 'The event could not be changed.') }),
  });
  const add = useMutation({
    mutationFn: createEvent,
    onSuccess: async result => {
      setAddFormVersion(version => version + 1);
      const scheduledAhead = result.verification && !result.verification.ok;
      setStatus({
        tone: 'ok',
        text: scheduledAhead
          ? 'Event added ahead of time. It will remain scheduled until the Zoom room starts.'
          : `${result.event.topic || 'Event'} added. It is ready to go live.`,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.events });
    },
    onError: error => setStatus({ tone: 'error', text: errorMessage(error, 'The event could not be added.') }),
  });

  const visibleGroups = useMemo(() => groupEvents(events.data ?? [], viewDate, today), [events.data, today, viewDate]);
  const pendingEventId = action.isPending ? action.variables.event.id : null;

  function removeEvent(event: EventSummary) {
    const approved = window.confirm(
      `Remove ${event.topic || event.room_name || 'this room'} from Events?\n\nIts chat data will remain in the Data archive.`,
    );
    if (approved) action.mutate({ kind: 'archive', event });
  }

  return (
    <ModulePage
      id="events-title"
      title="Events"
      className="events-manager"
      innerClassName="events-manager__inner"
      meta={events.data ? <span className="events-count">{events.data.length} registered</span> : null}
      actions={(
        <button className="icon-button events-refresh" type="button" title="Refresh events" aria-label="Refresh events" disabled={events.isFetching} onClick={() => void events.refetch()}>
          <RefreshCw className={events.isFetching ? 'spin' : ''} size={17} />
        </button>
      )}
    >
      {status ? (
        <div className={`events-feedback ${status.tone}`} role={status.tone === 'error' ? 'alert' : 'status'}>
          <span>{status.text}</span>
          <button className="icon-button" type="button" title="Dismiss status" aria-label="Dismiss status" onClick={() => setStatus(null)}><X size={14} /></button>
        </div>
      ) : null}

      <div className="events-layout">
          <main className="events-list-panel">
            <div className="events-calendar-bar">
              <div className="segmented-control" role="group" aria-label="Event date filter">
                <button className={viewDate === null ? 'active' : ''} type="button" aria-pressed={viewDate === null} onClick={() => setViewDate(null)}>All</button>
                <button className={viewDate === today ? 'active' : ''} type="button" aria-pressed={viewDate === today} onClick={() => setViewDate(today)}>Today</button>
              </div>
              <button className="icon-button" type="button" title="Previous day" aria-label="Previous day" onClick={() => setViewDate(stepDate(viewDate, -1))}><ChevronLeft size={17} /></button>
              <button className="icon-button" type="button" title="Next day" aria-label="Next day" onClick={() => setViewDate(stepDate(viewDate, 1))}><ChevronRight size={17} /></button>
              <span>{viewDate === null ? 'All events' : dayLabel(viewDate, today)}</span>
            </div>

            {events.isPending ? <div className="native-module__state" role="status"><LoaderCircle className="spin" size={20} />Loading events</div> : null}
            {events.isError ? (
              <div className="native-module__state native-module__state--error" role="alert">
                <AlertCircle size={22} /><strong>Events could not be loaded</strong>
                <span>{errorMessage(events.error, 'Try refreshing the event list.')}</span>
                <button className="secondary-button" type="button" onClick={() => void events.refetch()}>Try again</button>
              </div>
            ) : null}

            {events.data && visibleGroups.length === 0 ? (
              <div className="native-module__state events-empty">
                <CalendarDays size={24} />
                <strong>{viewDate === null ? 'No events added yet' : `Nothing scheduled for ${dayLabel(viewDate, today)}`}</strong>
                <span>{viewDate === null ? 'Add an event to prepare a room for your team.' : 'Browse another day or return to all events.'}</span>
              </div>
            ) : null}

            <div className="events-groups">
              {visibleGroups.map(group => (
                <section className="events-group" aria-label={group.label || 'Events'} key={group.key}>
                  {group.label ? <h2 id={`event-group-${group.key}`}>{group.label}</h2> : null}
                  <div className="events-cards">
                    {group.events.map(event => (
                      <EventCard
                        event={event}
                        key={event.id}
                        pending={pendingEventId === event.id}
                        onAction={value => action.mutate(value)}
                        onDialog={setDialog}
                        onRemove={() => removeEvent(event)}
                      />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </main>

          <aside className="events-sidebar" aria-label="Event setup">
            <AddEventForm key={addFormVersion} pending={add.isPending} onAdd={input => add.mutate(input)} />
            <ZoomConnectionPanel
              loading={zoomStatus.isPending || zoomStatus.isFetching}
              status={zoomStatus.data}
              error={zoomStatus.isError ? errorMessage(zoomStatus.error, 'Connection status is unavailable.') : null}
              onRefresh={() => void zoomStatus.refetch()}
            />
          </aside>
      </div>

      {dialog?.kind === 'edit' ? <EditEventDialog event={dialog.event} pending={action.isPending} onCancel={() => setDialog(null)} onSave={input => action.mutate({ kind: 'update', event: dialog.event, input })} /> : null}
      {dialog?.kind === 'schedule' ? <ScheduleEventDialog event={dialog.event} pending={action.isPending} onCancel={() => setDialog(null)} onSave={scheduledAt => action.mutate({ kind: 'schedule', event: dialog.event, scheduledAt })} /> : null}
      {dialog?.kind === 'link' ? <ReplyBotDialog event={dialog.event} pending={action.isPending} onCancel={() => setDialog(null)} onSave={joinUrl => action.mutate({ kind: 'link', event: dialog.event, joinUrl })} /> : null}
    </ModulePage>
  );
}

function AddEventForm({ pending, onAdd }: { pending: boolean; onAdd: (input: EventCreateInput) => void }) {
  const [account, setAccount] = useState('1');
  const [type, setType] = useState<'meeting' | 'webinar'>('meeting');
  const [zoomId, setZoomId] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [topic, setTopic] = useState('');
  const [roomName, setRoomName] = useState('');
  const [teamShared, setTeamShared] = useState(true);
  const [validation, setValidation] = useState('');

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!zoomId.trim()) {
      setValidation('Enter a meeting or webinar ID.');
      return;
    }
    setValidation('');
    onAdd({
      zoom_account: Number(account),
      event_type: type,
      zoom_id: zoomId.trim(),
      ...(topic.trim() ? { topic: topic.trim() } : {}),
      ...(roomName.trim() ? { room_name: roomName.trim() } : {}),
      team_shared: teamShared,
      ...(scheduledAt ? { scheduled_at: new Date(scheduledAt).getTime() } : {}),
    });
  };

  return (
    <section className="events-tool" aria-labelledby="add-event-title">
      <header><CalendarClock size={17} /><h2 id="add-event-title">Add event</h2></header>
      <form onSubmit={submit}>
        <div className="events-form-grid">
          <label><span>Zoom account</span><select value={account} onChange={event => setAccount(event.target.value)}><option value="1">Account 1</option><option value="2">Account 2</option></select></label>
          <label><span>Type</span><select value={type} onChange={event => setType(event.target.value as 'meeting' | 'webinar')}><option value="meeting">Meeting</option><option value="webinar">Webinar</option></select></label>
        </div>
        <label><span>Meeting or webinar ID</span><input inputMode="numeric" placeholder="891 2345 6789" value={zoomId} onChange={event => setZoomId(event.target.value)} /></label>
        <label><span>Date and time <small>optional</small></span><input type="datetime-local" value={scheduledAt} onChange={event => setScheduledAt(event.target.value)} /></label>
        <label><span>Display name <small>optional</small></span><input placeholder="Filled from Zoom when available" value={topic} onChange={event => setTopic(event.target.value)} /></label>
        <label><span>Room label <small>optional</small></span><input placeholder="Moderation room name" value={roomName} onChange={event => setRoomName(event.target.value)} /></label>
        <label className="events-check"><input type="checkbox" checked={teamShared} onChange={event => setTeamShared(event.target.checked)} /><span>Share with my team</span></label>
        {validation ? <span className="events-form-error" role="alert">{validation}</span> : null}
        <button className="primary-button" type="submit" disabled={pending}>{pending ? <LoaderCircle className="spin" size={16} /> : <CalendarDays size={16} />}Add event</button>
      </form>
    </section>
  );
}

function EventCard({ event, pending, onAction, onDialog, onRemove }: { event: EventSummary; pending: boolean; onAction: (action: EventAction) => void; onDialog: (dialog: EventDialog) => void; onRemove: () => void }) {
  const [exportsOpen, setExportsOpen] = useState(false);
  const room = event.room_name || event.topic || `Event ${event.id}`;
  const status = eventStatusLabels[event.status || 'unknown'] || 'Scheduled';
  return (
    <article className={`event-card ${event.active ? '' : 'paused'}`}>
      <header>
        <div className="event-card__identity">
          {event.scheduled_at ? <span className="event-time"><CalendarClock size={13} />{timeLabel(event.scheduled_at)}</span> : null}
          <h3>{event.topic || 'Untitled event'}</h3>
        </div>
        <span className={`event-status ${status.toLowerCase()}`}><i />{status}</span>
      </header>
      <div className="event-badges">
        <span>Acct {event.zoom_account}</span><span>{event.event_type}</span>
        {event.is_vip ? <span className="vip"><Star size={11} fill="currentColor" />VIP</span> : null}
        {event.auto_discovered ? <span>Auto</span> : null}
        {!event.active ? <span className="paused">Paused</span> : null}
        {event.recall_bot_id ? <span className="bot">Reply bot live</span> : null}
      </div>
      <dl className="event-meta">
        <div><dt>Zoom ID</dt><dd>{event.zoom_id}</dd></div>
        <div><dt>Room</dt><dd>{room}</dd></div>
        <div><dt>Last chat</dt><dd>{formatDateTime(event.last_seen)}</dd></div>
        <div><dt>Reply bots</dt><dd>{event.join_url ? (event.bot_enabled ? 'Allowed' : 'Off') : 'No link'}</dd></div>
      </dl>
      <div className="event-actions">
        <button className={event.active ? 'secondary-button warning' : 'primary-button compact'} type="button" disabled={pending} onClick={() => onAction({ kind: 'toggle', event })}>{event.active ? <Pause size={14} /> : <Play size={14} />}{event.active ? 'Pause' : 'Go live'}</button>
        <button className="secondary-button" type="button" disabled={pending} onClick={() => onDialog({ kind: 'schedule', event })}><CalendarClock size={14} />{event.scheduled_at ? 'Reschedule' : 'Set date'}</button>
        <button className="secondary-button" type="button" disabled={pending} onClick={() => onDialog({ kind: 'edit', event })}><Pencil size={14} />Edit</button>
        <button className="secondary-button" type="button" disabled={pending} onClick={() => onDialog({ kind: 'link', event })}><Link2 size={14} />{event.join_url ? 'Zoom link' : 'Enable bot'}</button>
        {event.join_url ? <button className="secondary-button" type="button" disabled={pending} aria-pressed={event.bot_enabled} onClick={() => onAction({ kind: 'style', event, input: { bot_enabled: !event.bot_enabled }, message: `Reply bots ${event.bot_enabled ? 'disabled' : 'enabled'} for ${room}.` })}><Video size={14} />Bots {event.bot_enabled ? 'on' : 'off'}</button> : null}
        <button className={`secondary-button ${event.team_shared ? 'active' : ''}`} type="button" disabled={pending} aria-pressed={event.team_shared} onClick={() => onAction({ kind: 'style', event, input: { team_shared: !event.team_shared }, message: `${room} is now ${event.team_shared ? 'private' : 'shared with the team'}.` })}><Users size={14} />{event.team_shared ? 'Team' : 'Private'}</button>
        <button className={`secondary-button ${event.is_vip ? 'vip' : ''}`} type="button" disabled={pending} aria-pressed={event.is_vip} onClick={() => onAction({ kind: 'style', event, input: { is_vip: !event.is_vip }, message: `${room} ${event.is_vip ? 'removed from' : 'added to'} VIP rooms.` })}><Star size={14} fill={event.is_vip ? 'currentColor' : 'none'} />VIP</button>
        <label className="event-color" title="Room color"><span>Color</span><input type="color" aria-label={`${room} color`} disabled={pending} value={safeColor(event.color)} onChange={change => onAction({ kind: 'style', event, input: { color: change.target.value }, message: `${room} color updated.` })} /></label>
        <button className="secondary-button" type="button" aria-expanded={exportsOpen} disabled={pending} onClick={() => setExportsOpen(open => !open)}><Download size={14} />Chat</button>
        <button className="secondary-button" type="button" disabled={pending} onClick={() => onAction({ kind: 'verify', event })}><ShieldCheck size={14} />Verify</button>
        <button className="danger-button" type="button" disabled={pending} onClick={onRemove}><Trash2 size={14} />Remove</button>
      </div>
      {exportsOpen ? (
        <div className="event-exports" aria-label={`${room} chat downloads`}>
          <span>Download</span>
          <a href={`/api/events/${event.id}/export?format=txt`}><FileText size={14} />Text</a>
          <a href={`/api/events/${event.id}/export?format=csv`}><FileSpreadsheet size={14} />CSV</a>
          <a href={`/api/events/${event.id}/export?format=json`}><FileJson size={14} />JSON</a>
        </div>
      ) : null}
      {pending ? <span className="event-card__pending" role="status"><LoaderCircle className="spin" size={14} />Saving</span> : null}
    </article>
  );
}

function ZoomConnectionPanel({ loading, status, error, onRefresh }: { loading: boolean; status: Awaited<ReturnType<typeof getZoomConnectionStatus>> | undefined; error: string | null; onRefresh: () => void }) {
  return (
    <section className="events-tool zoom-connections" aria-labelledby="zoom-connection-title">
      <header><Video size={17} /><h2 id="zoom-connection-title">Zoom connection</h2></header>
      {status?.granted ? <p className="zoom-granted">Using Zoom access from {status.grantedFrom || 'your organization'}.</p> : null}
      {loading && !status ? <div className="zoom-connection-state"><LoaderCircle className="spin" size={16} />Checking connections</div> : null}
      {error ? <div className="zoom-connection-error" role="alert"><AlertCircle size={15} />{error}</div> : null}
      {status?.accounts.map(account => (
        <div className={`zoom-account ${account.ok ? 'ok' : account.configured ? 'bad' : 'idle'}`} key={account.account}>
          {account.ok ? <CheckCircle2 size={16} /> : <CircleOff size={16} />}
          <div><strong>Account {account.account}</strong><span>{account.ok ? 'Authenticated' : account.configured ? account.reason || 'Authentication failed' : 'Not connected'}</span></div>
        </div>
      ))}
      <button className="secondary-button full-width" type="button" disabled={loading} onClick={onRefresh}><RefreshCw className={loading ? 'spin' : ''} size={14} />Re-check connection</button>
    </section>
  );
}

function EditEventDialog({ event, pending, onCancel, onSave }: { event: EventSummary; pending: boolean; onCancel: () => void; onSave: (input: EventUpdateInput) => void }) {
  const [topic, setTopic] = useState(event.topic || '');
  const [roomName, setRoomName] = useState(event.room_name || '');
  const [zoomId, setZoomId] = useState(event.zoom_id);
  const [account, setAccount] = useState(String(event.zoom_account));
  const [type, setType] = useState(event.event_type);
  const [validation, setValidation] = useState('');
  const submit = (formEvent: FormEvent<HTMLFormElement>) => {
    formEvent.preventDefault();
    if (!zoomId.trim()) { setValidation('Zoom ID is required.'); return; }
    onSave({ topic: topic.trim(), room_name: roomName.trim(), zoom_id: zoomId.trim(), zoom_account: Number(account), event_type: type });
  };
  return (
    <DialogFrame title={`Edit ${event.topic || 'event'}`} onCancel={onCancel}>
      <form className="event-dialog-form" onSubmit={submit}>
        <label><span>Event name</span><input autoFocus value={topic} onChange={change => setTopic(change.target.value)} /></label>
        <label><span>Room label</span><input value={roomName} onChange={change => setRoomName(change.target.value)} /></label>
        <label><span>Zoom ID</span><input value={zoomId} onChange={change => setZoomId(change.target.value)} /></label>
        <div className="events-form-grid"><label><span>Account</span><select value={account} onChange={change => setAccount(change.target.value)}><option value="1">Account 1</option><option value="2">Account 2</option></select></label><label><span>Type</span><select value={type} onChange={change => setType(change.target.value as 'meeting' | 'webinar')}><option value="meeting">Meeting</option><option value="webinar">Webinar</option></select></label></div>
        {validation ? <span className="events-form-error" role="alert">{validation}</span> : null}
        <DialogActions pending={pending} onCancel={onCancel} submitLabel="Save event" />
      </form>
    </DialogFrame>
  );
}

function ScheduleEventDialog({ event, pending, onCancel, onSave }: { event: EventSummary; pending: boolean; onCancel: () => void; onSave: (scheduledAt: number) => void }) {
  const [value, setValue] = useState(() => toLocalInput(event.scheduled_at));
  return (
    <DialogFrame title={`Schedule ${event.topic || 'event'}`} onCancel={onCancel}>
      <form className="event-dialog-form" onSubmit={formEvent => { formEvent.preventDefault(); if (value) onSave(new Date(value).getTime()); }}>
        <label><span>Date and time</span><input autoFocus required type="datetime-local" value={value} onChange={change => setValue(change.target.value)} /></label>
        <DialogActions pending={pending} onCancel={onCancel} submitLabel="Save schedule" disabled={!value} />
      </form>
    </DialogFrame>
  );
}

function ReplyBotDialog({ event, pending, onCancel, onSave }: { event: EventSummary; pending: boolean; onCancel: () => void; onSave: (joinUrl: string) => void }) {
  const [value, setValue] = useState(event.join_url);
  return (
    <DialogFrame title={`Zoom link for ${event.topic || 'event'}`} onCancel={onCancel}>
      <form className="event-dialog-form" onSubmit={formEvent => { formEvent.preventDefault(); onSave(value.trim()); }}>
        <label><span>Full Zoom join URL</span><input autoFocus type="url" placeholder="https://zoom.us/j/...?..." value={value} onChange={change => setValue(change.target.value)} /></label>
        <p>Include the complete URL so an approved reply bot can enter the room. Saving an empty value removes the link and turns bots off.</p>
        <DialogActions pending={pending} onCancel={onCancel} submitLabel={value.trim() ? 'Save link' : 'Remove link'} />
      </form>
    </DialogFrame>
  );
}

function DialogFrame({ title, children, onCancel }: { title: string; children: React.ReactNode; onCancel: () => void }) {
  return <div className="event-dialog-backdrop" role="presentation"><div className="event-dialog" role="dialog" aria-modal="true" aria-label={title}><header><h2>{title}</h2><button className="icon-button" type="button" title="Close" aria-label="Close" onClick={onCancel}><X size={16} /></button></header>{children}</div></div>;
}

function DialogActions({ pending, onCancel, submitLabel, disabled = false }: { pending: boolean; onCancel: () => void; submitLabel: string; disabled?: boolean }) {
  return <div className="event-dialog-actions"><button className="secondary-button" type="button" disabled={pending} onClick={onCancel}>Cancel</button><button className="primary-button compact" type="submit" disabled={pending || disabled}>{pending ? <LoaderCircle className="spin" size={15} /> : null}{submitLabel}</button></div>;
}

async function executeEventAction(action: EventAction): Promise<{ message: string }> {
  const room = action.event.room_name || action.event.topic || `Event ${action.event.id}`;
  switch (action.kind) {
    case 'toggle': {
      const result = await toggleEvent(action.event.id);
      return { message: `${room} is now ${result.active ? 'live' : 'paused'}.` };
    }
    case 'verify': {
      const result = await verifyEvent(action.event.id);
      return { message: result.verification?.ok ? `${room} verified with Zoom.` : `${room} remains scheduled. ${result.verification?.reason || 'Zoom has not reported it live yet.'}` };
    }
    case 'archive':
      await archiveEvent(action.event.id);
      return { message: `${room} removed from Events. Its chat remains in Data.` };
    case 'style':
      await styleEvent(action.event.id, action.input);
      return { message: action.message };
    case 'schedule':
      await styleEvent(action.event.id, { scheduled_at: action.scheduledAt });
      return { message: `${room} rescheduled for ${formatDateTime(action.scheduledAt)}.` };
    case 'link':
      await styleEvent(action.event.id, { join_url: action.joinUrl, bot_enabled: Boolean(action.joinUrl) });
      return { message: action.joinUrl ? `Reply bot link saved for ${room}.` : `Reply bot link removed from ${room}.` };
    case 'update':
      await updateEvent(action.event.id, action.input);
      return { message: `${room} updated.` };
  }
}

function groupEvents(events: EventSummary[], viewDate: number | null, today: number): Array<{ key: string; label: string; events: EventSummary[] }> {
  if (viewDate !== null) {
    const end = stepDate(viewDate, 1);
    const inDay = events.filter(event => event.scheduled_at != null && event.scheduled_at >= viewDate && event.scheduled_at < end).sort(sortScheduled);
    return inDay.length ? [{ key: dayKey(viewDate), label: '', events: inDay }] : [];
  }
  const scheduled = events.filter(event => event.scheduled_at != null).sort(sortScheduled);
  const undated = events.filter(event => event.scheduled_at == null).sort((left, right) => left.id - right.id);
  const groups = new Map<string, { key: string; label: string; events: EventSummary[] }>();
  for (const event of scheduled) {
    const key = dayKey(event.scheduled_at!);
    const group = groups.get(key) ?? { key, label: dayLabel(event.scheduled_at!, today), events: [] };
    group.events.push(event);
    groups.set(key, group);
  }
  const result = [...groups.values()];
  if (undated.length) result.push({ key: 'undated', label: scheduled.length ? 'No date set' : '', events: undated });
  return result;
}

function sortScheduled(left: EventSummary, right: EventSummary): number {
  return (left.scheduled_at ?? 0) - (right.scheduled_at ?? 0);
}

function localMidnight(value: number): number {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function stepDate(value: number | null, days: number): number {
  const date = new Date(value ?? localMidnight(Date.now()));
  date.setDate(date.getDate() + days);
  return localMidnight(date.getTime());
}

function dayKey(value: number): string {
  const date = new Date(value);
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

function dayLabel(value: number, today: number): string {
  const day = localMidnight(value);
  const tomorrow = stepDate(today, 1);
  const yesterday = stepDate(today, -1);
  if (day === today) return 'Today';
  if (day === tomorrow) return 'Tomorrow';
  if (day === yesterday) return 'Yesterday';
  return new Intl.DateTimeFormat(undefined, { weekday: 'short', month: 'short', day: 'numeric', ...(day < today ? { year: 'numeric' } : {}) }).format(new Date(value));
}

function timeLabel(value: number): string {
  return new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(new Date(value));
}

function formatDateTime(value: number | null): string {
  if (!value) return '-';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(value));
}

function toLocalInput(value: number | null): string {
  if (!value) return '';
  const date = new Date(value);
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function safeColor(value: string): string {
  return /^#[0-9a-f]{6}$/i.test(value) ? value : '#6b7280';
}
